import { useEffect, useState, useCallback } from "react";
import { Smartphone, Share, PlusSquare, Download, X } from "lucide-react";

/**
 * Install prompt for the Care Rooms area — a targeted variant of
 * InstallShiftKit that switches the active <link rel="manifest"> to the
 * Care-Rooms-specific manifest while this component is mounted.
 *
 * Why swap the manifest link at mount time:
 *   Chromium reads whichever <link rel="manifest"> is in the DOM at the
 *   moment of install. The default site manifest is /manifest.webmanifest
 *   (start_url = /shift-kit). This component temporarily activates
 *   /manifest-care-rooms.webmanifest (start_url = /care-rooms) so a user
 *   who installs while inside the Care Rooms area gets a Care Rooms
 *   home-screen icon that launches to /care-rooms.
 *
 * Auth on a cold-launch is handled by the existing HandoverProtectedRoute
 * around /care-rooms — an expired session is redirected via
 * ?next=%2Fcare-rooms, the checking page then window.location.replace()s
 * back to /care-rooms after redemption. No auth code changes required.
 *
 * Nothing is cached client-side. There is no service worker in this app.
 * That means: NO private conversations, documents, authenticated API
 * responses or sensitive Care Room content can be cached for offline
 * access by this component (or by the app at all). This is by design.
 */

const DISMISS_KEY = "jcc-care-rooms-install-dismissed";
const CARE_ROOMS_MANIFEST = "/manifest-care-rooms.webmanifest";
const DEFAULT_MANIFEST = "/manifest.webmanifest";

function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.navigator.standalone === true) return true;
  return false;
}

function detectIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIOSUA = /iPad|iPhone|iPod/.test(ua);
  const isIPadOS = ua.includes("Macintosh") && typeof document !== "undefined" && "ontouchend" in document;
  return isIOSUA || isIPadOS;
}

/** Point <link rel="manifest"> at the Care-Rooms manifest for the lifetime
 *  of this component; restore the previous value on unmount so the rest
 *  of the site (and Shift Kit's install flow on /shift-kit) is unaffected. */
function useCareRoomsManifest() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return undefined;
    const previous = link.getAttribute("href") || DEFAULT_MANIFEST;
    link.setAttribute("href", CARE_ROOMS_MANIFEST);
    return () => {
      link.setAttribute("href", previous);
    };
  }, []);
}

export default function InstallCareRooms() {
  useCareRoomsManifest();

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    setInstalled(isStandalone());
    setIsIOS(detectIOS());
    try {
      if (window.localStorage.getItem(DISMISS_KEY) === "1") setDismissed(true);
    } catch { /* ignore */ }

    if (window.__jccDeferredInstallPrompt) {
      setDeferredPrompt(window.__jccDeferredInstallPrompt);
    }

    const onCustom = () => {
      if (window.__jccDeferredInstallPrompt) {
        setDeferredPrompt(window.__jccDeferredInstallPrompt);
      }
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("jcc:beforeinstallprompt", onCustom);
    window.addEventListener("jcc:appinstalled", onInstalled);
    return () => {
      window.removeEventListener("jcc:beforeinstallprompt", onCustom);
      window.removeEventListener("jcc:appinstalled", onInstalled);
    };
  }, []);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice && choice.outcome === "accepted") setInstalled(true);
      } catch { /* user cancelled or unsupported */ }
      window.__jccDeferredInstallPrompt = null;
      setDeferredPrompt(null);
      return;
    }
    if (isIOS) setShowIOSHelp((v) => !v);
  }, [deferredPrompt, isIOS]);

  if (installed || dismissed) return null;

  const showAndroidInstall = !!deferredPrompt;
  const showIOSCard = isIOS && !deferredPrompt;
  const showFallback = !showAndroidInstall && !showIOSCard;

  return (
    <div
      data-testid="install-care-rooms-card"
      className="rounded-2xl border border-purple/20 bg-purple/5 p-4 sm:p-5 mb-8 flex items-start gap-4"
    >
      <div className="w-10 h-10 rounded-xl bg-white text-purple flex items-center justify-center shrink-0 shadow-sm">
        <Smartphone className="w-5 h-5" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-display text-base sm:text-lg font-semibold text-ink">
            Add Care Rooms to your home screen
          </h3>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss install prompt"
            data-testid="install-care-rooms-dismiss"
            className="text-ink-muted hover:text-ink rounded-full p-1 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-1 text-sm text-ink-muted">
          Open Care Rooms like an app — one tap from your phone, straight to your rooms.
          Nothing about your rooms or conversations is stored on the device.
        </p>

        {showAndroidInstall && (
          <div className="mt-4">
            <button
              type="button"
              onClick={handleInstall}
              data-testid="install-care-rooms-android-button"
              className="btn-purple text-sm py-2.5"
            >
              <Download className="w-4 h-4" />
              Add Care Rooms to Home Screen
            </button>
          </div>
        )}

        {showIOSCard && (
          <div className="mt-4">
            <button
              type="button"
              onClick={handleInstall}
              data-testid="install-care-rooms-ios-button"
              className="btn-outline text-sm py-2.5"
              aria-expanded={showIOSHelp}
            >
              <Smartphone className="w-4 h-4" />
              {showIOSHelp ? "Hide instructions" : "How to add on iPhone / iPad"}
            </button>
            {showIOSHelp && (
              <ol
                data-testid="install-care-rooms-ios-instructions"
                className="mt-4 space-y-2 text-sm text-ink"
              >
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-white border border-purple/30 text-purple text-xs font-semibold flex items-center justify-center shrink-0">1</span>
                  <span className="flex-1">In Safari, tap the <Share className="inline w-4 h-4 mx-1 -mt-0.5" strokeWidth={1.75} /> <strong>Share</strong> button at the bottom of the screen.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-white border border-purple/30 text-purple text-xs font-semibold flex items-center justify-center shrink-0">2</span>
                  <span className="flex-1">Scroll and tap <PlusSquare className="inline w-4 h-4 mx-1 -mt-0.5" strokeWidth={1.75} /> <strong>Add to Home Screen</strong>.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-white border border-purple/30 text-purple text-xs font-semibold flex items-center justify-center shrink-0">3</span>
                  <span className="flex-1">Tap <strong>Add</strong> in the top-right corner. Care Rooms will open from your home screen.</span>
                </li>
              </ol>
            )}
          </div>
        )}

        {showFallback && (
          <p className="mt-3 text-xs text-ink-muted">
            Open this page on your phone (Chrome on Android or Safari on iPhone / iPad) to add Care Rooms to your home screen.
          </p>
        )}
      </div>
    </div>
  );
}
